// src/pages/ProxyChannels.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import MDEditor, { commands } from '@uiw/react-md-editor';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

interface Account {
  id: string;
  name: string;
  phone_number: string;
  status: 'AUTHORIZED' | 'INACTIVE';
}

interface JobResponse {
  job_id: string;
}

interface JobStatus {
  task_id: string;
  state: 'PENDING' | 'SUCCESS' | 'FAILURE';
  result?: any;
  error?: string;
}

const AccountPickerMultiById: React.FC<{
  accounts: Account[];
  selected: string[];
  onChange: (ids: string[]) => void;
  groups?: AccountGroup[];
}> = ({ accounts, selected, onChange, groups }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const authorized = accounts.filter(acc => acc.status === 'AUTHORIZED');

  // Группы несут name, а пикер работает по id — резолвим name -> id
  // только по авторизованным аккаунтам (неавторизованные молча отбрасываются)
  const nameToId = new Map(authorized.map(acc => [acc.name, acc.id]));
  const groupIds = (group: AccountGroup): string[] =>
    group.linked_accounts
      .map(a => nameToId.get(a.name))
      .filter((id): id is string => Boolean(id));

  // Добавить аккаунты группы к уже выбранным
  const applyGroup = (group: AccountGroup) => {
    const ids = groupIds(group);
    const toAdd = ids.filter(id => !selected.includes(id));
    if (toAdd.length > 0) onChange([...selected, ...toAdd]);
  };

  // Заменить выбор на аккаунты группы
  const replaceWithGroup = (group: AccountGroup) => {
    onChange(groupIds(group));
  };

  const filtered = authorized.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).slice(0, 50);

  const filteredIds = filtered.map(a => a.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.includes(id));
  const totalFiltered = authorized.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).length;

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter(i => i !== id) : [...selected, id]);

  const toggleAllFiltered = () => {
    if (allFilteredSelected) onChange(selected.filter(id => !filteredIds.includes(id)));
    else onChange([...selected, ...filteredIds.filter(id => !selected.includes(id))]);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box onClick={() => setOpen(o => !o)} sx={{
        width: '100%', border: open ? '2px solid #1E40AF' : '1px solid #C4C4C4',
        borderRadius: 1, px: 1.5, py: 1, minHeight: 56, cursor: 'pointer',
        display: 'flex', alignItems: 'center', boxSizing: 'border-box', bgcolor: 'white',
        '&:hover': { borderColor: '#1E40AF' },
      }}>
        {selected.length === 0
          ? <Typography color="text.secondary">Выберите аккаунты</Typography>
          : <Typography sx={{ color: '#1E40AF', fontWeight: 500 }}>Выбрано {selected.length} из {authorized.length} аккаунтов</Typography>
        }
      </Box>
      {open && (
        <>
          <Box onClick={() => { setOpen(false); setSearch(''); }} sx={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
          <Box sx={{
            position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1300,
            bgcolor: 'white', border: '1px solid #E5E7EB', borderRadius: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5,
            display: 'flex', flexDirection: 'column', maxHeight: 480,
          }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField autoFocus size="small" sx={{ flex: 1 }} placeholder="Поиск по имени или номеру..."
                value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); toggleAllFiltered(); }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                {allFilteredSelected ? 'Снять отфильтр.' : `Выбрать все (${filtered.length})`}
              </Button>
              {selected.length > 0 && (
                <Button size="small" color="error" variant="outlined" onClick={(e) => { e.stopPropagation(); onChange([]); }}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                  Снять все
                </Button>
              )}
            </Box>

            {/* Быстрый выбор по группам */}
            {groups && groups.length > 0 && (
              <Box sx={{
                px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB',
                display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                bgcolor: '#EFF6FF',
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
                  Группы:
                </Typography>
                {groups.map((group, idx) => {
                  const colors = ['#DBEAFE', '#D1FAE5', '#FEF3C7', '#FEE2E2', '#EDE9FE'];
                  const color = colors[idx % colors.length];
                  const available = groupIds(group).length;       // сколько реально доступно (авториз.)
                  const total = group.linked_accounts.length;
                  return (
                    <Chip
                      key={group.id}
                      label={available === total ? `${group.name} (${total})` : `${group.name} (${available}/${total})`}
                      size="medium"
                      variant="filled"
                      disabled={available === 0}
                      onClick={(e) => { e.stopPropagation(); applyGroup(group); }}
                      onDelete={(e) => { e.stopPropagation(); replaceWithGroup(group); }}
                      deleteIcon={
                        <Box component="span" sx={{ fontSize: '0.75rem', px: 0.25 }} title="Заменить выбор на эту группу">
                          ↺
                        </Box>
                      }
                      sx={{
                        cursor: 'pointer', bgcolor: color,
                        '&:hover': { bgcolor: '#BFDBFE' },
                        fontWeight: 500, fontSize: '0.85rem',
                      }}
                    />
                  );
                })}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontStyle: 'italic' }}>
                  клик — добавить, ↺ — заменить
                </Typography>
              </Box>
            )}

            <Box sx={{ overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start' }}>
              {filtered.length === 0
                ? <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center', gridColumn: '1 / -1' }}>Ничего не найдено</Typography>
                : filtered.map(acc => (
                  <MenuItem key={acc.id} dense onClick={(e) => { e.stopPropagation(); toggle(acc.id); }}
                    selected={selected.includes(acc.id)} sx={{ py: 0.75, px: 1.5 }}>
                    <Checkbox size="small" checked={selected.includes(acc.id)} sx={{ p: 0.5, mr: 1 }} />
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={selected.includes(acc.id) ? 600 : 400} noWrap>{acc.name}</Typography>
                      {acc.phone_number && <Typography variant="caption" color="text.secondary" noWrap>{acc.phone_number}</Typography>}
                    </Box>
                  </MenuItem>
                ))
              }
              {totalFiltered > 50 && (
                <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: '#6B7280', textAlign: 'center', gridColumn: '1 / -1' }}>
                  Показаны первые 50 — уточните поиск
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">{search ? `Найдено: ${totalFiltered}` : `Авторизовано: ${authorized.length}`}</Typography>
              <Typography variant="caption" fontWeight={600} color="#1E40AF">Выбрано: {selected.length}</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const ProxyChannels: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'split'>('split');
  const [showEmoji, setShowEmoji] = useState(false);

  // Форма
  const [form, setForm] = useState({
    channel_title: '',
    base_username: '',
    description: '',
    first_message: '',
    forward_links: '', // теперь через запятую
    hide_forward_source: false,
    attach_to_profile: true,
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  // Задача и мониторинг
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);

  // Поллинг статуса
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get<JobStatus>(`parse/job_status/${jobId}`);
        setJobStatus(res.data);

        if (res.data.state === 'SUCCESS' || res.data.state === 'FAILURE') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Ошибка проверки статуса:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [jobId]);

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAccounts();
  }, [auth?.isAuthenticated, navigate]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/accounts');
      setAccounts(res.data.accounts || []);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
    } catch (err: any) {
      setMessage({ text: 'Ошибка загрузки аккаунтов', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (selectedAccounts.length === 0) {
      setMessage({ text: 'Выберите хотя бы один аккаунт', type: 'error' });
      return;
    }

    if (!form.channel_title.trim() || !form.base_username.trim()) {
      setMessage({ text: 'Укажите название и базовый username', type: 'error' });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();

    selectedAccounts.forEach(id => {
  formData.append('account_ids', id);
});
    formData.append('channel_title', form.channel_title);
    formData.append('base_username', form.base_username);
    formData.append('description', form.description);
    if (form.first_message.trim()) formData.append('first_message', form.first_message);

    // Ссылки через запятую
    if (form.forward_links.trim()) {
      const links = form.forward_links
        .split(',')
        .map(l => l.trim())
        .filter(Boolean);
      formData.append('forward_links', JSON.stringify(links));
    }

    formData.append('hide_forward_source', String(form.hide_forward_source));
    formData.append('attach_to_profile', String(form.attach_to_profile));

    if (avatarFile) {
      formData.append('avatar', avatarFile);
    }

    try {
      const res = await api.post<JobResponse>('accounts/proxy_channels', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobId(res.data.job_id);
      setMessage({ text: `Задача создана! ID: ${res.data.job_id}`, type: 'success' });
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка запуска задачи', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setMessage({ text: 'Только изображения', type: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: 'Файл слишком большой (макс 5 МБ)', type: 'error' });
        return;
      }
      setAvatarFile(file);
    }
  };

  const addEmoji = (emoji: any) => {
    setForm(prev => ({
      ...prev,
      first_message: (prev.first_message || '') + emoji.native
    }));
    setShowEmoji(false);
  };

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#1E3A8A', fontWeight: 600 }}>
        Создание каналов-прокладок
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Выбор аккаунтов */}
          <Grid size={12}>
            <AccountPickerMultiById
              accounts={accounts}
              selected={selectedAccounts}
              onChange={setSelectedAccounts}
              groups={groups}
            />
          </Grid>

          {/* Название канала */}
          <Grid size={12}>
            <TextField
              label="Название канала"
              value={form.channel_title}
              onChange={(e) => setForm({ ...form, channel_title: e.target.value })}
              fullWidth
              helperText="Поддерживает {name} — будет заменено на имя аккаунта"
            />
          </Grid>

          {/* Базовый username */}
          <Grid size={12}>
            <TextField
              label="Базовый username (без @)"
              value={form.base_username}
              onChange={(e) => setForm({ ...form, base_username: e.target.value })}
              fullWidth
              helperText="Поддерживает {name}. При коллизии добавится рандомный суффикс"
            />
          </Grid>

          {/* Описание */}
          <Grid size={12}>
            <TextField
              label="Описание канала"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="До 255 символов. Поддерживает {name}"
            />
          </Grid>

          {/* Первое сообщение — Markdown + эмодзи (точно как в MassSending) */}
          <Grid size={12}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              flexWrap: 'wrap'  // ← добавить это
            }}>
              <Typography variant="subtitle1" fontWeight={600} color="#1E40AF">
                Первое сообщение в канале
              </Typography>
              <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EmojiEmotionsIcon/>}
                  onClick={() => setShowEmoji(p => !p)}
              >
                Эмодзи
              </Button>
              <Box sx={{
                ml: {xs: 0, sm: 'auto'},  // ← на мобилке убрать auto-отступ
                display: 'flex',
                bgcolor: '#f1f5f9',
                borderRadius: 2,
                p: 0.5,
                flexShrink: 0
              }}>
                <Button size="small" variant={viewMode === 'edit' ? 'contained' : 'text'}
                        onClick={() => setViewMode('edit')}>
                  Редактировать
                </Button>
                <Button size="small" variant={viewMode === 'preview' ? 'contained' : 'text'}
                        onClick={() => setViewMode('preview')}>
                  Превью
                </Button>
                <Button size="small" variant={viewMode === 'split' ? 'contained' : 'text'}
                        onClick={() => setViewMode('split')}>
                  Рядом
                </Button>
              </Box>
            </Box>

            {showEmoji && (
              <Box sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
                <Picker
                  data={data}
                  onEmojiSelect={addEmoji}
                  theme="light"
                  previewPosition="none"
                />
              </Box>
            )}

            <Box sx={{ border: '1px solid #d0d7de', borderRadius: 2, overflow: 'hidden' }}>
              <MDEditor
                value={form.first_message}
                onChange={val => setForm(prev => ({ ...prev, first_message: val || '' }))}
                preview={viewMode === 'edit' ? 'edit' : viewMode === 'preview' ? 'preview' : 'live'}
                height={viewMode === 'split' ? 520 : 420}
                hideToolbar={viewMode === 'preview'}
                textareaProps={{
                  placeholder:
                    '**Привет!**\nЭто канал-прокладка.\nПодпишись на основной: [t.me/mainchan]\n{name} — твой ник',
                }}
                commands={[
                  commands.bold,
                  commands.italic,
                  commands.strikethrough,
                  commands.link,
                  commands.quote,
                  commands.code,
                  commands.codeBlock,
                ]}
                extraCommands={[]}
                visibleDragbar={false}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Поддерживает Markdown, {`{name}`} заменится на имя аккаунта
            </Typography>
          </Grid>

          {/* Ссылки для репоста — через запятую */}
          <Grid size={12}>
            <TextField
              label="Ссылки на сообщения для репоста (через запятую)"
              value={form.forward_links}
              onChange={(e) => setForm({ ...form, forward_links: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="https://t.me/channel/123, https://t.me/channel/456, https://t.me/chan/789"
              helperText="Формат: https://t.me/channel_name/message_id — разделяйте запятыми"
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.hide_forward_source}
                  onChange={(e) => setForm({ ...form, hide_forward_source: e.target.checked })}
                />
              }
              label="Скрыть источник репоста (посты будут выглядеть как оригинальные)"
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.attach_to_profile}
                  onChange={(e) => setForm({ ...form, attach_to_profile: e.target.checked })}
                />
              }
              label="Прикрепить созданный канал к профилю аккаунта"
            />
          </Grid>

          {/* Аватарка */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              Аватарка (одна на все каналы)
            </Typography>
            <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} fullWidth sx={{ mb: 2 }}>
              {avatarFile ? avatarFile.name : 'Загрузить аватарку'}
              <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
            </Button>
            {avatarFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img
                  src={URL.createObjectURL(avatarFile)}
                  alt="preview"
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                />
                <IconButton onClick={() => setAvatarFile(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Grid>

          {/* Кнопка запуска */}
          <Grid size={12}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleCreate}
              disabled={isLoading || selectedAccounts.length === 0}
              sx={{ py: 2, bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
            >
              {isLoading ? 'Создание...' : 'Создать прокси-каналы'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Мониторинг задачи */}
      {jobId && (
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Статус задачи: {jobId}
          </Typography>

          {jobStatus ? (
            <>
              <Typography variant="subtitle1">
                Состояние: <strong>{jobStatus.state}</strong>
              </Typography>

              {jobStatus.state === 'PENDING' && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Создание каналов... (проверка каждые 3 сек)</Typography>
                </Box>
              )}

              {jobStatus.state === 'SUCCESS' && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  Успешно завершено!
                  {jobStatus.result && (
                    <pre style={{ marginTop: 16, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                      {JSON.stringify(jobStatus.result, null, 2)}
                    </pre>
                  )}
                </Alert>
              )}

              {jobStatus.state === 'FAILURE' && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  Ошибка: {jobStatus.error || 'Неизвестная ошибка'}
                </Alert>
              )}
            </>
          ) : (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography sx={{ mt: 2 }}>Загрузка статуса...</Typography>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default ProxyChannels;